/**
 * Service for LLM provider, model, and configuration API calls.
 */

import { BaseService } from './BaseService';
import type {
  LlmProviderDto,
  LlmModelDto,
  LlmProviderConfigDto,
  CreateProviderConfigRequest,
  UpdateProviderConfigRequest,
} from '../types/llm';

export class LlmService extends BaseService {
  // --- Providers (read-only catalog) ---

  async getProviders(isActive?: boolean): Promise<LlmProviderDto[]> {
    const params = new URLSearchParams();
    if (isActive !== undefined) params.append('isActive', isActive.toString());
    const url = `/llm-providers${params.toString() ? `?${params.toString()}` : ''}`;
    return this.get<LlmProviderDto[]>(url);
  }

  async getProvider(id: string): Promise<LlmProviderDto> {
    return this.get<LlmProviderDto>(`/llm-providers/${id}`);
  }

  // --- Models (read-only catalog) ---

  async getModels(providerId?: string, isActive?: boolean): Promise<LlmModelDto[]> {
    const params = new URLSearchParams();
    if (providerId) params.append('providerId', providerId);
    if (isActive !== undefined) params.append('isActive', isActive.toString());
    const url = `/llm-models${params.toString() ? `?${params.toString()}` : ''}`;
    return this.get<LlmModelDto[]>(url);
  }

  async getAvailableModels(): Promise<LlmModelDto[]> {
    return this.get<LlmModelDto[]>('/llm-models/available');
  }

  // --- Provider Configs (tenant CRUD) ---

  async getProviderConfigs(): Promise<LlmProviderConfigDto[]> {
    return this.get<LlmProviderConfigDto[]>('/llm-provider-configs');
  }

  async getProviderConfig(id: string): Promise<LlmProviderConfigDto> {
    return this.get<LlmProviderConfigDto>(`/llm-provider-configs/${id}`);
  }

  async createProviderConfig(data: CreateProviderConfigRequest): Promise<LlmProviderConfigDto> {
    return this.post<LlmProviderConfigDto>('/llm-provider-configs', data);
  }

  async updateProviderConfig(id: string, data: UpdateProviderConfigRequest): Promise<LlmProviderConfigDto> {
    return this.put<LlmProviderConfigDto>(`/llm-provider-configs/${id}`, data);
  }

  async deleteProviderConfig(id: string): Promise<void> {
    return this.delete<void>(`/llm-provider-configs/${id}`);
  }
}

export const llmService = new LlmService();
